import { useMemo, useState } from "react";
import { usePackages, type PackageSummary } from "../hooks/usePackages";
import styles from "./Packages.module.css";

type Tab = "active" | "archived";

function daysUntilPurge(archivedAt: number): number {
  const purgeAt = archivedAt + 30 * 24 * 60 * 60;
  const nowSec = Math.floor(Date.now() / 1000);
  return Math.max(0, Math.ceil((purgeAt - nowSec) / (24 * 60 * 60)));
}

function formatDate(unixSec: number): string {
  return new Date(unixSec * 1000).toLocaleDateString();
}

export default function Packages() {
  const {
    active,
    archived,
    loading,
    error,
    update,
    archive,
    restore,
    hardDelete,
  } = usePackages();
  const [tab, setTab] = useState<Tab>("active");
  const [editing, setEditing] = useState<PackageSummary | null>(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const rows = useMemo(
    () => (tab === "active" ? active : archived),
    [tab, active, archived],
  );

  function openEdit(pkg: PackageSummary) {
    setEditing(pkg);
    setForm({ name: pkg.name, description: pkg.description });
    setFormError("");
  }

  async function save() {
    if (!editing) return;
    setSaving(true);
    setFormError("");
    try {
      await update(editing.id, {
        name: form.name,
        description: form.description,
      });
      setEditing(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(id: string) {
    if (
      !confirm(
        "Archive this package? It will be permanently deleted after 30 days.",
      )
    )
      return;
    await archive(id);
  }

  async function handleHardDelete(id: string) {
    if (!confirm("Permanently delete this package? This cannot be undone."))
      return;
    await hardDelete(id);
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.headerTitle}>PACKAGES</h1>
        <div className={styles.headerLine} />
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${tab === "active" ? styles.tabActive : ""}`}
          onClick={() => setTab("active")}
        >
          Active ({active.length})
        </button>
        <button
          className={`${styles.tab} ${tab === "archived" ? styles.tabActive : ""}`}
          onClick={() => setTab("archived")}
        >
          Archived ({archived.length})
        </button>
      </div>

      {error && <p className={styles.formError}>{error}</p>}

      <div className={styles.card}>
        {loading ? (
          <p className={styles.empty}>Loading…</p>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>
            {tab === "active" ? "No packages yet" : "Recycle bin is empty"}
          </p>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHead}>
              <tr>
                <th>Name</th>
                <th>Items</th>
                <th>Types</th>
                <th>Source portal</th>
                <th>Created by</th>
                <th>Created</th>
                {tab === "archived" && <th>Auto-delete</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className={styles.tableRow}>
                  <td>
                    <p className={styles.name}>{p.name}</p>
                    {p.description && (
                      <p className={styles.desc}>{p.description}</p>
                    )}
                  </td>
                  <td className={styles.meta}>{p.itemCount}</td>
                  <td className={styles.meta}>{p.types}</td>
                  <td className={styles.meta}>
                    {p.sourcePortalNickname ?? p.sourcePortalId}
                  </td>
                  <td className={styles.meta}>{p.createdBy}</td>
                  <td className={styles.meta}>{formatDate(p.createdAt)}</td>
                  {tab === "archived" && (
                    <td className={styles.meta}>
                      in {daysUntilPurge(p.archivedAt ?? 0)} days
                    </td>
                  )}
                  <td>
                    <div className={styles.actionsCell}>
                      {tab === "active" ? (
                        <>
                          <button
                            className={styles.actionBtn}
                            onClick={() => openEdit(p)}
                          >
                            Edit
                          </button>
                          <button
                            className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                            onClick={() => void handleArchive(p.id)}
                          >
                            Archive
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className={styles.actionBtn}
                            onClick={() => void restore(p.id)}
                          >
                            Restore
                          </button>
                          <button
                            className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                            onClick={() => void handleHardDelete(p.id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>Edit package</h2>
            {formError && <p className={styles.formError}>{formError}</p>}
            <div className={styles.formField}>
              <label className={styles.formLabel}>Name</label>
              <input
                className={styles.formInput}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Description</label>
              <textarea
                className={styles.formTextarea}
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
            <div className={styles.modalActions}>
              <button
                className={styles.btnSecondary}
                disabled={saving}
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>
              <button
                className={styles.btnPrimary}
                disabled={saving}
                onClick={() => void save()}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
